// ─── Client-side high-intent web event emitter ──────────────────────────────
// Sends events to POST /api/web-events (server publishes to Event Grid).
// No secrets here — all auth happens server-side.

import { isAnalyticsAllowed } from './consentGate';

const API_BASE = window.REACT_APP_DEMO_REQUESTS_API || '';

// Track which visits have already been sent this page session to avoid duplicates
const sentVisits = new Set();

/**
 * Normalize a pathname:
 * 1) trim whitespace
 * 2) ensure leading "/"
 * 3) remove trailing "/" unless path is exactly "/"
 */
function normalizePath(raw) {
  let p = (raw || '').trim();
  if (!p || p === '/') return '/';
  if (!p.startsWith('/')) p = '/' + p;
  // Strip query string and hash
  p = p.split('?')[0].split('#')[0];
  // Remove trailing slash
  if (p.length > 1 && p.endsWith('/')) p = p.slice(0, -1);
  return p;
}

/**
 * Collect current UTM params from the URL.
 */
function getUtmParams() {
  try {
    const params = new URLSearchParams(window.location.search);
    const utm = {};
    for (const [key, value] of params) {
      if (key.startsWith('utm_')) utm[key] = value;
    }
    return Object.keys(utm).length ? utm : undefined;
  } catch (_) {
    return undefined;
  }
}

/**
 * Send a web event to the server (fire-and-forget).
 * Uses sendBeacon when possible, else fetch with keepalive.
 *
 * @param {string} eventName — one of: zerivo.web.visit, zerivo.web.apply_click, zerivo.web.form_submit
 * @param {object} opts      — { path, metadata, referrer }
 */
export function emitWebEvent(eventName, opts = {}) {
  if (!isAnalyticsAllowed()) return;

  const rawPath = opts.path || window.location.pathname;
  const path = normalizePath(rawPath);

  if (path.startsWith('/api/')) return;

  const payload = {
    event_name: eventName,
    path,
    referrer: opts.referrer || (document.referrer || ''),
    utm: getUtmParams(),
    metadata: opts.metadata || {},
  };

  const payloadStr = JSON.stringify(payload);
  const url = `${API_BASE}/api/web-events`;

  // Use sendBeacon if available (fire-and-forget)
  if (navigator.sendBeacon) {
    try {
      const blob = new Blob([payloadStr], { type: 'application/json' });
      if (navigator.sendBeacon(url, blob)) return;
    } catch (_) {
      // Fall through to fetch
    }
  }

  // Fallback: fetch with keepalive
  fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: payloadStr,
    keepalive: true,
  }).catch(() => {
    // Silently fail — must never block user experience
  });
}

/**
 * Emit a visit event for high-intent pages.
 * Only fires once per path per page session (guards against React strict mode / rerenders).
 *
 * @param {string} rawPath — current pathname
 */
export function emitVisitIfHighIntent(rawPath) {
  const path = normalizePath(rawPath);
  const ALLOWED = ['/contact'];
  const match = ALLOWED.some((p) => path === p || path.startsWith(p + '/'));
  if (!match) return;
  if (sentVisits.has(path)) return;
  sentVisits.add(path);
  emitWebEvent('zerivo.web.visit', { path });
}

/**
 * Emit an apply_click event.
 *
 * @param {string} cta      — button label text
 * @param {string} location — where the button is (e.g. "pricing", "design-partner")
 */
export function emitApplyClick(cta, location) {
  emitWebEvent('zerivo.web.apply_click', {
    path: window.location.pathname,
    metadata: { cta, location },
  });
}
